/**
 * MeasurementLine + CameraFocus — small standalone 3D scene helpers
 * extracted from `scene.js` (iter 42 refactor) to keep that file shrinking
 * toward the 1000-line target. Both are pure presentational components:
 * MeasurementLine renders a labeled distance line between two equipment-list
 * items; CameraFocus is a side-effect-only `useThree()` consumer that
 * repositions the camera when `focusTarget` changes.
 *
 * Convention: uses `React.createElement` (`h` alias) — see scene.js for
 * the rationale (visual-edits babel plugin breaks JSX on R3F nodes).
 */
import React, { useEffect } from "react";
import { Html } from "@react-three/drei";
import { useThree } from "@react-three/fiber";
import * as THREE from "three";
import { IN_TO_M } from "./scene";

const h = React.createElement;

export function MeasurementLine({ a, b }) {
  if (!a?.position || !b?.position) return null;
  const ax = (a.position.x_in + (a.position.w_in || a.length_in || 24) / 2) * IN_TO_M;
  const az = (a.position.y_in + (a.position.h_in || a.width_in  || 18) / 2) * IN_TO_M;
  const ay = ((a.depth_in || 84) / 2) * IN_TO_M;
  const bx = (b.position.x_in + (b.position.w_in || b.length_in || 24) / 2) * IN_TO_M;
  const bz = (b.position.y_in + (b.position.h_in || b.width_in  || 18) / 2) * IN_TO_M;
  const by = ((b.depth_in || 84) / 2) * IN_TO_M;
  const geo = new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(ax, ay, az), new THREE.Vector3(bx, by, bz),
  ]);
  const dist_in = Math.hypot(
    (a.position.x_in + (a.position.w_in || a.length_in || 24) / 2) - (b.position.x_in + (b.position.w_in || b.length_in || 24) / 2),
    (a.position.y_in + (a.position.h_in || a.width_in  || 18) / 2) - (b.position.y_in + (b.position.h_in || b.width_in  || 18) / 2),
  );
  const label = h("div", {
    style: {
      background: "#00E5FF", color: "#000",
      fontFamily: "ui-monospace, monospace", fontSize: 11, fontWeight: 700,
      padding: "3px 7px", border: "1px solid #00E5FF",
      whiteSpace: "nowrap", pointerEvents: "none",
    },
  }, `${dist_in.toFixed(1)}" / ${(dist_in / 12).toFixed(2)} ft`);
  return h("group", null,
    h("line", { geometry: geo },
      h("lineBasicMaterial", { color: "#00E5FF", linewidth: 2 }),
    ),
    h(Html, { position: [(ax + bx) / 2, (ay + by) / 2 + 0.3, (az + bz) / 2], center: true, pointerEvents: "none" }, label),
  );
}

export function CameraFocus({ focusTarget }) {
  const { camera } = useThree();
  useEffect(() => {
    if (!focusTarget) return;
    if (focusTarget.kind === "tray") {
      const tx = focusTarget.x_in * IN_TO_M;
      const ty = focusTarget.y_in * IN_TO_M;
      const tz = focusTarget.z_in * IN_TO_M;
      const offset = focusTarget.offset_m || 5.0;
      camera.position.set(tx + offset, ty + offset * 0.6, tz + offset);
      camera.lookAt(tx, ty, tz);
      return;
    }
    const tx = (focusTarget.position?.x_in + (focusTarget.position?.w_in || focusTarget.length_in || 24) / 2) * IN_TO_M;
    const tz = (focusTarget.position?.y_in + (focusTarget.position?.h_in || focusTarget.width_in  || 18) / 2) * IN_TO_M;
    const ty = ((focusTarget.depth_in || 84) / 2) * IN_TO_M;
    const offset = 3;
    camera.position.set(tx + offset, ty + offset * 0.8, tz + offset);
    camera.lookAt(tx, ty, tz);
  }, [focusTarget]); // eslint-disable-line
  return null;
}
